import React, { useMemo } from 'react';
import { Table } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import CustomTooltip from "../Components/Modals/CustomTooltip";
import EmptyCard from "../EmptyCard";
import { variantTokens, primaryShades } from "../../theme/theme";
import { formatNumberBritish } from "../../utils/numberFormatting";
import LoadingCard from '../Components/Cards/LoadingCard';

const EventsAnalytics = ({ data, loading, clearFiltersVisible, onResetFilters, calculationFunction }) => {

    // Utilizziamo la funzione di calcolo che viene passata dalla pagina principale
    const tableData = useMemo(() => {
        // Se è disponibile una funzione di calcolo esterna, la utilizziamo
        if (calculationFunction && typeof calculationFunction === 'function') {
            try {
                return calculationFunction(data);
            } catch (error) {
                console.error("Error using the calculation function:", error);
                // In case of error, use the original logic
            }
        }

        // Logica originale se non c'è una funzione di calcolo o si è verificato un errore
        return processEventsData(data);
    }, [data, calculationFunction]);

    const processEventsData = (dataCollection) => {
        if (!dataCollection || dataCollection.length === 0) return [];

        const combinedAnalytics = dataCollection.flatMap((item) =>
            (item.events_analytics_list || []).map((event) => ({
                ...event,
                device_id: item.device_id,
            }))
        );

        const eventMap = combinedAnalytics.reduce((acc, event) => {
            Object.entries(event).forEach(([key, value]) => {
                if (
                    key !== 'device_id' &&
                    key !== 'brand_page_scroll_perc' &&
                    key !== 'acceptance_terms' &&
                    (typeof value === 'number' ? value > 0 : value === true)
                ) {
                    if (!acc[key]) {
                        acc[key] = { eventName: key, eventCount: 0, uniqueUsers: new Set() };
                    }
                    acc[key].eventCount += value === true ? 1 : value;
                    acc[key].uniqueUsers.add(event.device_id);
                }
            });
            return acc;
        }, {});

        return Object.values(eventMap).map((event, index) => {
            const { eventName, eventCount, uniqueUsers } = event;
            const totalUsers = uniqueUsers.size;
            const avgEventPerUser = totalUsers > 0 ? (eventCount / totalUsers).toFixed(2) : 0;

            return {
                key: `${eventName}_${index}`, // Assicuriamo che la chiave sia univoca
                event: formatEventName(eventName),
                count: eventCount,
                totalUsers,
                avgEventPerUser,
            };
        }).sort((a, b) => b.count - a.count); // Ordina per Event Count in modo decrescente
    };

    const formatEventName = (eventName) => {
        const formattedName = eventName
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .toLowerCase();

        return formattedName.charAt(0).toUpperCase() + formattedName.slice(1);
    };

    // Verifica se ci sono dati da visualizzare
    const hasData = tableData && tableData.length > 0;

    const columns = [
        {
            title: (
                <span>
                    Event{' '}
                    <CustomTooltip title="The name of the event being tracked.">
                        <InfoCircleOutlined />
                    </CustomTooltip>
                </span>
            ),
            dataIndex: 'event',
            key: 'event',
            sorter: (a, b) => a.event.localeCompare(b.event),
            showSorterTooltip: false,
            width: '40%',
        },
        {
            title: (
                <span>
                    Count{' '}
                    <CustomTooltip title="Total number of times this event occurred.">
                        <InfoCircleOutlined />
                    </CustomTooltip>
                </span>
            ),
            dataIndex: 'count',
            key: 'count',
            sorter: (a, b) => a.count - b.count,
            defaultSortOrder: 'descend',
            showSorterTooltip: false,
            width: '20%',
            render: (value) => formatNumberBritish(value, 0),
        },
        {
            title: (
                <span>
                    Users{' '}
                    <CustomTooltip title="Number of unique users who triggered this event.">
                        <InfoCircleOutlined />
                    </CustomTooltip>
                </span>
            ),
            dataIndex: 'totalUsers',
            key: 'totalUsers',
            sorter: (a, b) => a.totalUsers - b.totalUsers,
            showSorterTooltip: false,
            width: '20%',
            render: (value) => formatNumberBritish(value, 0),
        },
        {
            title: (
                <span>
                    Avg/User{' '}
                    <CustomTooltip title="Average number of events per user.">
                        <InfoCircleOutlined />
                    </CustomTooltip>
                </span>
            ),
            dataIndex: 'avgEventPerUser',
            key: 'avgEventPerUser',
            sorter: (a, b) => a.avgEventPerUser - b.avgEventPerUser,
            showSorterTooltip: false,
            width: '20%',
            render: (value) => formatNumberBritish(value, 2),
        },
    ];

    // Renderizza EmptyCard quando non ci sono dati
    if (!loading && !hasData) {
        return <EmptyCard />;
    }

    return (
        <div style={{ width: "100%" }}>
            {loading ? (
                <LoadingCard />
            ) : (

                <div className="table-container">
                    <Table
                        dataSource={tableData}
                        rowKey="key"
                        columns={columns}
                        pagination={{
                            defaultPageSize: 10,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100', '200', '500', '1000'],
                        }}
                        size="small"
                        scroll={{ x: "max-content" }}
                        className="analytics-table"
                    />
                </div>
            )}
            <style jsx="true">{`
                .table-container {
                    border: 1px solid #d9d9d9;
                    border-radius: 8px;
                    overflow: hidden;
                }
                .analytics-table .ant-pagination-item {
                    background: transparent;
                    border: none;
                }
                .analytics-table .ant-pagination-item-active {
                    background: ${primaryShades.primary300};
                }
                .analytics-table .ant-pagination-item-active a {
                    color: ${variantTokens.white};
                }
                .analytics-table .ant-pagination {
                    padding-right: 16px;
                }
                .analytics-table th.ant-table-cell {
                    text-align: left;
                }
                .analytics-table td.ant-table-cell {
                    text-align: left;
                }
                .analytics-table th.ant-table-cell[aria-sort]:not([aria-sort="none"]) {
                    background: ${primaryShades.primary50};
                }
                .analytics-table .ant-table-column-sorter-up.active,
                .analytics-table .ant-table-column-sorter-down.active {
                    color: ${primaryShades.primary500};
                }
            `}</style>
        </div>
    );
};

export default EventsAnalytics;